import { createGlobalReducerState } from "@/lib/state/createGlobalReducerState";
import { useStorage, type StorageService } from "@/lib/storage";
import dayjs from "dayjs";
import { useCallback, useEffect } from "react";

export interface BulkTransactionsState {
	[key: string]: BulkTransactionsDraft | undefined;
}

export interface BulkTransactionsDraft {
	itemsLeaving: BulkTransactionsEntry[];
	itemsEntering: BulkTransactionsEntry[];
	cashLeaving: number;
	cashEntering: number;
	occurredDate: string;
}

export interface BulkTransactionsEntry {
	productId: string;
	variantId: string;
	quantity: number;
}

type BulkTransactionsAction = {
	type: "addLeavingItem";
	collectionId: string;
	productId: string;
	variantId: string;
	quantity: number;
} | {
	type: "addEnteringItem";
	collectionId: string;
	productId: string;
	variantId: string;
	quantity: number;
} | {
	type: "removeLeavingItem";
	collectionId: string;
	variantId: string;
} | {
	type: "removeEnteringItem";
	collectionId: string;
	variantId: string
} | {
	type: "setLeavingItemQuantity";
	collectionId: string;
	variantId: string;
	quantity: number
} | {
	type: "setEnteringItemQuantity";
	collectionId: string;
	variantId: string;
	quantity: number
} | {
	type: "setCashLeaving";
	collectionId: string;
	cash: number
} | {
	type: "setCashEntering";
	collectionId: string;
	cash: number
} | {
	type: "setOccurredDate";
	collectionId: string;
	occurredDate: string
} | {
	type: "reset";
	collectionId: string
};

const getInitialState = (storage: StorageService): BulkTransactionsState => {
	return storage.getPersistent<BulkTransactionsState>("bulkTransactionsState") || {};
};

const reducer = (state: BulkTransactionsState, action: BulkTransactionsAction): BulkTransactionsState => {
	switch (action.type) {
	case "addLeavingItem": {
		if (state[action.collectionId]?.itemsLeaving.find(x => x.variantId === action.variantId)) {
			return state;
		}

		return {
			...state,
			[action.collectionId]: {
				...state[action.collectionId] || emptyBulkTransactionsDraft(),
				itemsLeaving: [
					...state[action.collectionId]?.itemsLeaving || [],
					{
						productId: action.productId,
						variantId: action.variantId,
						quantity: action.quantity
					}
				]
			}
		};
	}

	case "addEnteringItem": {
		if (state[action.collectionId]?.itemsEntering.find(x => x.variantId === action.variantId)) {
			return state;
		}

		return {
			...state,
			[action.collectionId]: {
				...state[action.collectionId] || emptyBulkTransactionsDraft(),
				itemsEntering: [
					...state[action.collectionId]?.itemsEntering || [],
					{
						productId: action.productId,
						variantId: action.variantId,
						quantity: action.quantity
					}
				]
			}
		};
	}

	case "removeLeavingItem": {
		return {
			...state,
			[action.collectionId]: {
				...state[action.collectionId] || emptyBulkTransactionsDraft(),
				itemsLeaving: state[action.collectionId]?.itemsLeaving.filter(x => x.variantId !== action.variantId) || []
			}
		};
	}

	case "removeEnteringItem": {
		return {
			...state,
			[action.collectionId]: {
				...state[action.collectionId] || emptyBulkTransactionsDraft(),
				itemsEntering: state[action.collectionId]?.itemsEntering.filter(x => x.variantId !== action.variantId) || []
			}
		};
	}

	case "setLeavingItemQuantity": {
		return {
			...state,
			[action.collectionId]: {
				...state[action.collectionId] || emptyBulkTransactionsDraft(),
				itemsLeaving: state[action.collectionId]?.itemsLeaving.map(x => x.variantId === action.variantId ? { ...x, quantity: action.quantity } : x) || []
			}
		};
	}

	case "setEnteringItemQuantity": {
		return {
			...state,
			[action.collectionId]: {
				...state[action.collectionId] || emptyBulkTransactionsDraft(),
				itemsEntering: state[action.collectionId]?.itemsEntering.map(x => x.variantId === action.variantId ? { ...x, quantity: action.quantity } : x) || []
			}
		};
	}

	case "setCashLeaving": {
		return {
			...state,
			[action.collectionId]: {
				...state[action.collectionId] || emptyBulkTransactionsDraft(),
				cashLeaving: action.cash
			}
		};
	}

	case "setCashEntering": {
		return {
			...state,
			[action.collectionId]: {
				...state[action.collectionId] || emptyBulkTransactionsDraft(),
				cashEntering: action.cash
			}
		};
	}

	case "setOccurredDate": {
		return {
			...state,
			[action.collectionId]: {
				...state[action.collectionId] || emptyBulkTransactionsDraft(),
				occurredDate: action.occurredDate
			}
		};
	}

	case "reset":
		return {
			...state,
			[action.collectionId]: emptyBulkTransactionsDraft()
		};
	}
};

export const emptyBulkTransactionsDraft = (): BulkTransactionsDraft => ({
	itemsLeaving: [],
	itemsEntering: [],
	cashLeaving: 0,
	cashEntering: 0,
	occurredDate: dayjs().format("YYYY-MM-DD")
});

const [BulkTransactionsStateProvider, useBulkTransactionsStateReducer] = createGlobalReducerState(reducer, getInitialState);
export { BulkTransactionsStateProvider };

export const useBulkTransactionsState = () => {
	const storage = useStorage();
	const [state, dispatch] = useBulkTransactionsStateReducer();

	useEffect(() => {
		const timeout = setTimeout(() => {
			storage.setPersistent("bulkTransactionsState", state);
		}, 250);

		return () => clearTimeout(timeout);
	}, [state]);

	const dispatchAddLeavingItem = useCallback((collectionId: string, variantId: string, productId: string, quantity: number) => {
		dispatch({
			type: "addLeavingItem",
			collectionId,
			variantId,
			productId,
			quantity
		});
	}, [dispatch]);

	const dispatchAddEnteringItem = useCallback((collectionId: string, variantId: string, productId: string, quantity: number) => {
		dispatch({
			type: "addEnteringItem",
			collectionId,
			variantId,
			productId,
			quantity
		});
	}, [dispatch]);

	const dispatchRemoveLeavingItem = useCallback((collectionId: string, variantId: string) => {
		dispatch({
			type: "removeLeavingItem",
			collectionId,
			variantId
		});
	}, [dispatch]);

	const dispatchRemoveEnteringItem = useCallback((collectionId: string, variantId: string) => {
		dispatch({
			type: "removeEnteringItem",
			collectionId,
			variantId
		});
	}, [dispatch]);

	const dispatchSetLeavingItemQuantity = useCallback((collectionId: string, variantId: string, quantity: number) => {
		dispatch({
			type: "setLeavingItemQuantity",
			collectionId,
			variantId,
			quantity
		});
	}, [dispatch]);

	const dispatchSetEnteringItemQuantity = useCallback((collectionId: string, variantId: string, quantity: number) => {
		dispatch({
			type: "setEnteringItemQuantity",
			collectionId,
			variantId,
			quantity
		});
	}, [dispatch]);

	const dispatchSetCashLeaving = useCallback((collectionId: string, cash: number) => {
		dispatch({
			type: "setCashLeaving",
			collectionId,
			cash
		});
	}, [dispatch]);

	const dispatchSetCashEntering = useCallback((collectionId: string, cash: number) => {
		dispatch({
			type: "setCashEntering",
			collectionId,
			cash
		});
	}, [dispatch]);

	const dispatchSetOccurredDate = useCallback((collectionId: string, occurredDate: string) => {
		dispatch({
			type: "setOccurredDate",
			collectionId,
			occurredDate
		});
	}, [dispatch]);

	const dispatchReset = useCallback((collectionId: string) => {
		dispatch({
			type: "reset",
			collectionId
		});
	}, [dispatch]);

	return {
		state,
		dispatchAddLeavingItem,
		dispatchAddEnteringItem,
		dispatchRemoveLeavingItem,
		dispatchRemoveEnteringItem,
		dispatchSetLeavingItemQuantity,
		dispatchSetEnteringItemQuantity,
		dispatchSetCashLeaving,
		dispatchSetCashEntering,
		dispatchSetOccurredDate,
		dispatchReset
	};
};
